import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import {
  DataService,
  FormInputComponent,
  NotificationService,
  RelationCustomFieldConfig,
  SharedModule,
} from "@vendure/admin-ui/core";
import { fromEvent, Subject, takeUntil } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from "rxjs/operators";
import { gql } from "graphql-tag";
import { of } from "rxjs";

@Component({
  imports: [SharedModule],
  selector: "certification-form",
  template: `
    <div class="certification-form-wrapper">
      <!-- Asset Relation Input -->
      <vdr-relation-asset-input
        [config]="config"
        [parentFormControl]="formControl"
        [readonly]="readonly"
      ></vdr-relation-asset-input>

      <!-- Certificate Description Textarea -->
      <div class="mt-3" *ngIf="formControl.value">
        <label for="certificateDescription" class="form-label">
          Certificate Description
        </label>
        <textarea
          id="certificateDescription"
          name="certificateDescription"
          class="form-control"
          rows="4"
          placeholder="Enter certificate description..."
          [formControl]="descriptionControl"
          [readonly]="readonly"
        ></textarea>
        <small class="form-text text-muted">
          This description will be saved to the asset's custom fields.
        </small>
      </div>
    </div>
  `,
  styles: [
    `
      .certification-form-wrapper {
        width: 100%;
      }
      .mt-3 {
        margin-top: 1rem;
      }
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
      }
      .form-control {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-family: inherit;
        resize: vertical;
      }
      .form-control:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
      }
      .form-control[readonly] {
        background-color: #f8f9fa;
        opacity: 1;
      }
      .form-text {
        font-size: 0.875em;
        color: #6c757d;
        margin-top: 0.25rem;
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificationFormComponent
  implements OnInit, OnDestroy, FormInputComponent<RelationCustomFieldConfig>
{
  readonly: boolean;
  config: RelationCustomFieldConfig;
  formControl: UntypedFormControl;
  descriptionControl = new UntypedFormControl("");

  private destroy$ = new Subject<void>();
  private isUpdating = false;

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.formControl.value) {
      const id = this.formControl.value.id;
      this.dataService
        .query(
          gql`
            query GetAssetCustomFields($id: ID!) {
              asset(id: $id) {
                id
                customFields {
                  certificateDescription
                }
              }
            }
          `,
          { id },
        )
        .mapSingle((data) => data)
        .subscribe((result: any) => {
          if (result?.asset?.customFields?.certificateDescription) {
            this.descriptionControl.setValue(
              result.asset.customFields.certificateDescription,
              { emitEvent: false },
            );
          } else {
            this.descriptionControl.setValue("", { emitEvent: false });
          }
          this.cdr.detectChanges();
        });
    }

    // Initialize description from asset's customFields when asset changes
    this.initializeDescriptionFromAsset();

    // Subscribe to value changes
    this.setupValueChangeHandlers();

    // Listen for form submissions to save description
    this.setupFormSubmissionHandler();
  }

  private initializeDescriptionFromAsset(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap((asset) => {
          if (asset?.id) {
            // Fetch the asset to get its current customFields
            return this.dataService.query(
              gql`
                query GetAssetCustomFields($id: ID!) {
                  asset(id: $id) {
                    id
                    customFields {
                      certificateDescription
                    }
                  }
                }
              `,
              { id: asset.id },
            ).single$;
          }
          return of(null);
        }),
      )
      .subscribe((result: any) => {
        if (result?.asset?.customFields?.certificateDescription) {
          this.descriptionControl.setValue(
            result.data.asset.customFields.certificateDescription,
            { emitEvent: false },
          );
          this.cdr.detectChanges();
        } else {
          this.descriptionControl.setValue("", { emitEvent: false });
        }
      });
  }

  private setupValueChangeHandlers(): void {
    // Log asset changes
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {});

    // Auto-save description changes (debounced)
    this.descriptionControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000), // Wait 1 second after user stops typing
        distinctUntilChanged(),
        filter(() => !!this.formControl.value?.id && !this.isUpdating),
      )
      .subscribe((description) => {
        this.saveDescriptionToAsset(description);
      });
  }

  private setupFormSubmissionHandler(): void {
    // Listen for form submission events
    fromEvent(document, "submit")
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const form = event.target as HTMLFormElement;
        if (this.elementRef.nativeElement.closest("form") === form) {
          this.onFormSubmit();
        }
      });

    // Also listen for Update/Save button clicks
    fromEvent(document, "click")
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const target = event.target as HTMLElement;
        const buttonText = target.textContent?.trim().toLowerCase();

        if (
          target.tagName === "BUTTON" &&
          (buttonText?.includes("update") || buttonText?.includes("save"))
        ) {
          setTimeout(() => this.onFormSubmit(), 100); // Small delay to ensure form processing
        }
      });
  }

  private onFormSubmit(): void {
    if (this.formControl.value?.id && this.descriptionControl.value) {
      this.saveDescriptionToAsset(this.descriptionControl.value);
    } else if (this.formControl.value?.id && !this.descriptionControl.value) {
    } else {
    }
  }

  private saveDescriptionToAsset(description: string): void {
    const assetId = this.formControl.value?.id;
    if (!assetId || this.isUpdating) {
      return;
    }

    this.isUpdating = true;

    this.dataService
      .mutate(
        gql`
          mutation UpdateAssetCustomFields($input: UpdateAssetInput!) {
            updateAsset(input: $input) {
              id
              customFields {
                certificateDescription
              }
            }
          }
        `,
        {
          input: {
            id: assetId,
            customFields: {
              certificateDescription: description,
            },
          },
        },
      )
      .subscribe({
        next: (result) => {
          this.isUpdating = false;
          this.notificationService.success("Certificate description updated");
        },
        error: (error) => {
          this.isUpdating = false;
          this.notificationService.error(
            "Failed to update certificate description",
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
